import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/LanguageSelector/LanguageSelector";
import Spinner from "../../../components/Spinner/Spinner";
import { completeFederatedLogin } from "../../../services/federatedService";
import TokenService from "../../../services/token.service";
import useAuth from "../../../utils/hooks/useAuth";
import { PROVIDER_NAME_STORAGE_KEY, STATE_STORAGE_KEY } from "../federatedState";
import { federatedRefusalMessage } from "../../../utils/helpers/federatedErrors";
import { takeReturnTo } from "../../../utils/helpers/sessionHandoff";
import telosWordmark from "../../../assets/telos-wordmark.svg";

const CARD =
  "bg-white rounded-2xl shadow-xl shadow-slate-200/60 p-8 border border-slate-100";

export default function Callback() {
  const { t } = useTranslation();
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUserContext } = useAuth();
  const [error, setError] = useState(null);
  // StrictMode mounts effects twice in development. The authorization code is
  // single-use, so a second exchange would always fail and overwrite a
  // successful sign-in with an error.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const expectedState = sessionStorage.getItem(STATE_STORAGE_KEY);
    sessionStorage.removeItem(STATE_STORAGE_KEY);
    // The slug stands in when the name is missing — the route is all a callback
    // has to go on once storage is gone.
    const providerName = sessionStorage.getItem(PROVIDER_NAME_STORAGE_KEY) || provider;
    sessionStorage.removeItem(PROVIDER_NAME_STORAGE_KEY);

    const providerError = searchParams.get("error");
    if (providerError) {
      setError(t("auth.federated.providerDeclined"));
      return;
    }
    if (!code || !state) {
      setError(t("auth.federated.missingCode"));
      return;
    }
    // The server treats state as a single-use key and would reject a mismatch
    // anyway; checking here too discards a callback this browser never started
    // without spending the code at all.
    if (!expectedState || expectedState !== state) {
      setError(t("auth.federated.stateMismatch"));
      return;
    }

    completeFederatedLogin({ slug: provider, code, state })
      .then(async (tokens) => {
        TokenService.updateLocalAccessToken(tokens.access);
        TokenService.updateLocalRefreshToken(tokens.refresh);
        await setUserContext(tokens.access);
        // The link that sent them to the provider in the first place, if any.
        navigate(takeReturnTo() || "/", { replace: true });
      })
      .catch((err) => {
        // The API distinguishes a refusal (403 with a `code`) from a provider
        // outage (502). They need different words: one is "you cannot come in",
        // the other is "try again in a minute". A refusal is read by its code
        // — the server's `detail` is English, whatever language the page is in.
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 502) {
          setError(t("auth.federated.providerUnavailable"));
        } else {
          setError(
            federatedRefusalMessage(err, t, detail || t("auth.federated.failed"), {
              provider: providerName,
            })
          );
        }
      });
  }, [provider, searchParams, navigate, setUserContext, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-sky-50 to-blue-100 p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-3">
          <LanguageSelector />
        </div>
        <div className={`${CARD} text-center space-y-5`}>
          <img src={telosWordmark} alt="Telos" className="h-10 w-auto mx-auto" />
          {error ? (
            <>
              <p className="text-sm text-slate-600">{error}</p>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="w-full py-2.5 rounded-xl bg-[#0f2e54] text-white text-sm font-semibold shadow-lg shadow-[#0f2e54]/30 hover:bg-[#0c2543] transition"
              >
                {t("auth.federated.backToSignIn")}
              </button>
            </>
          ) : (
            <div className="flex justify-center py-4">
              <Spinner label={t("auth.federated.signingIn")} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
